import { useEffect, useState, useCallback } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Users, Building2, FileSignature, DollarSign,
  ShieldCheck, ScrollText, MessageSquare, Wrench, Activity,
  ChevronLeft, ChevronRight, Trash2, UserCog, Ban, CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar,
  XAxis, YAxis, CartesianGrid,
} from "recharts";

const COLORS = ["#0ea5e9", "#f59e0b", "#10b981", "#8b5cf6", "#ef4444", "#6366f1"];
const ROLE_LABELS = { tenant: "Tenant", admin: "Admin", finance: "Finance", operations: "Property Mgmt", super_admin: "System Admin" };
const ROLE_COLORS = { tenant: "bg-sky-50 text-sky-700", admin: "bg-amber-50 text-amber-700", finance: "bg-emerald-50 text-emerald-700", operations: "bg-violet-50 text-violet-700", super_admin: "bg-rose-50 text-rose-700" };

function KPI({ label, value, sub, icon: Icon, color = "text-sky-600" }) {
  return (
    <Card className="border-slate-200" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{label}</span>
          <Icon className={`h-4 w-4 ${color}`} />
        </div>
        <div className="font-heading text-2xl font-bold text-slate-900">{value}</div>
        {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

export default function SystemAdminDashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState({ logs: [], total: 0, page: 1, pages: 1 });
  const [forumPosts, setForumPosts] = useState({ posts: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [roleDialog, setRoleDialog] = useState(null);
  const [selectedRole, setSelectedRole] = useState("");
  const [purgeDialog, setPurgeDialog] = useState(false);
  const [purging, setPurging] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      const { data } = await api.get("/sysadmin/dashboard");
      setDashboard(data);
    } catch { /* ignore */ }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await api.get("/sysadmin/users");
      setUsers(data);
    } catch { /* ignore */ }
  }, []);

  const fetchAuditLogs = useCallback(async (page = 1) => {
    try {
      const { data } = await api.get(`/sysadmin/audit-logs?page=${page}&limit=15`);
      setAuditLogs(data);
    } catch { /* ignore */ }
  }, []);

  const fetchForumPosts = useCallback(async () => {
    try {
      const { data } = await api.get("/sysadmin/forum/posts?limit=30");
      setForumPosts(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    Promise.all([fetchDashboard(), fetchUsers(), fetchAuditLogs(), fetchForumPosts()])
      .finally(() => setLoading(false));
  }, [fetchDashboard, fetchUsers, fetchAuditLogs, fetchForumPosts]);

  const handleRoleChange = async () => {
    if (!roleDialog || !selectedRole) return;
    try {
      await api.put(`/sysadmin/users/${roleDialog.id}/role`, { role: selectedRole });
      toast.success(`Role updated to ${ROLE_LABELS[selectedRole]}`);
      setRoleDialog(null);
      fetchUsers();
      fetchDashboard();
      fetchAuditLogs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update role");
    }
  };

  const handleStatusToggle = async (userId, currentStatus) => {
    const newStatus = currentStatus === "active" ? "suspended" : "active";
    try {
      await api.put(`/sysadmin/users/${userId}/status`, { status: newStatus });
      toast.success(`User ${newStatus === "active" ? "activated" : "suspended"}`);
      fetchUsers();
      fetchDashboard();
      fetchAuditLogs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update status");
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm("Delete this post and all its comments?")) return;
    try {
      await api.delete(`/sysadmin/forum/posts/${postId}`);
      toast.success("Post deleted");
      fetchForumPosts();
      fetchDashboard();
      fetchAuditLogs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete post");
    }
  };

  const handlePurgeDatabase = async () => {
    setPurging(true);
    try {
      const { data } = await api.post("/sysadmin/purge-database");
      const s = data.summary || {};
      toast.success(`Purged ${s.tenant_users_deleted || 0} tenants. Re-seeded ${s.rooms_seeded || 0} rooms & ${s.forum_categories_seeded || 0} forum categories.`);
      setPurgeDialog(false);
      fetchDashboard();
      fetchUsers();
      fetchAuditLogs();
      fetchForumPosts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Purge failed");
    } finally {
      setPurging(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="System Administration">
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      </DashboardLayout>
    );
  }

  const d = dashboard || {};
  const roleData = (d.users?.by_role || []).map((r) => ({ name: ROLE_LABELS[r._id] || r._id, value: r.count }));

  return (
    <DashboardLayout title="System Administration">
      <div className="space-y-6" data-testid="sysadmin-dashboard">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-slate-100" data-testid="sysadmin-tabs">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">User Management</TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit">Audit Logs</TabsTrigger>
            <TabsTrigger value="forum" data-testid="tab-forum">Forum Moderation</TabsTrigger>
          </TabsList>

          {/* ============ OVERVIEW ============ */}
          <TabsContent value="overview" className="mt-6 space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              <KPI label="Total Users" value={d.users?.total || 0} sub={`${d.users?.active || 0} active`} icon={Users} color="text-sky-600" />
              <KPI label="Total Rooms" value={d.rooms || 0} icon={Building2} color="text-amber-600" />
              <KPI label="Active Leases" value={d.leases?.active || 0} sub={`${d.leases?.total || 0} total`} icon={FileSignature} color="text-indigo-600" />
              <KPI label="Revenue" value={`$${(d.revenue || 0).toLocaleString()}`} icon={DollarSign} color="text-emerald-600" />
              <KPI label="Open Tickets" value={d.maintenance || 0} icon={Wrench} color="text-red-500" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Users by Role Chart */}
              <Card className="border-slate-200">
                <CardHeader><CardTitle className="font-heading text-base font-bold">Users by Role</CardTitle></CardHeader>
                <CardContent>
                  {roleData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={240}>
                      <PieChart>
                        <Pie data={roleData} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                          {roleData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-center text-slate-400 py-12 text-sm">No user data</p>
                  )}
                </CardContent>
              </Card>

              {/* Recent Audit Activity */}
              <Card className="border-slate-200">
                <CardHeader><CardTitle className="font-heading text-base font-bold">Recent Activity</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-slate-100 max-h-[280px] overflow-y-auto">
                    {(d.recent_audit || []).slice(0, 10).map((log, i) => (
                      <div key={i} className="flex items-start gap-3 px-4 py-3">
                        <Activity className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm text-slate-700 font-medium truncate">{log.action?.replace(/_/g, " ")}</p>
                          <p className="text-xs text-slate-400 truncate">{log.details}</p>
                          <p className="text-[10px] text-slate-300 mt-0.5">{log.user_name} &middot; {new Date(log.created_at).toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                    {(d.recent_audit || []).length === 0 && <p className="text-center py-8 text-sm text-slate-400">No audit activity</p>}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Platform Stats Bar */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Card className="border-slate-200"><CardContent className="pt-4 pb-3 text-center">
                <p className="text-2xl font-bold text-slate-900 font-heading">{d.applications || 0}</p>
                <p className="text-xs text-slate-400 font-medium">Applications</p>
              </CardContent></Card>
              <Card className="border-slate-200"><CardContent className="pt-4 pb-3 text-center">
                <p className="text-2xl font-bold text-slate-900 font-heading">{d.payments || 0}</p>
                <p className="text-xs text-slate-400 font-medium">Payments</p>
              </CardContent></Card>
              <Card className="border-slate-200"><CardContent className="pt-4 pb-3 text-center">
                <p className="text-2xl font-bold text-slate-900 font-heading">{d.documents || 0}</p>
                <p className="text-xs text-slate-400 font-medium">Documents</p>
              </CardContent></Card>
              <Card className="border-slate-200"><CardContent className="pt-4 pb-3 text-center">
                <p className="text-2xl font-bold text-slate-900 font-heading">{d.forum_posts || 0}</p>
                <p className="text-xs text-slate-400 font-medium">Forum Posts</p>
              </CardContent></Card>
            </div>

            {/* Danger Zone */}
            <Card className="border-red-200 bg-red-50/30">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-base font-bold flex items-center gap-2 text-red-700">
                  <AlertTriangle className="h-5 w-5" /> Danger Zone
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-700">Purge Database</p>
                    <p className="text-xs text-slate-500 mt-0.5">Remove all tenant users and transactional data. Staff accounts are preserved. Rooms and forum categories are re-seeded.</p>
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    className="shrink-0 ml-4"
                    data-testid="purge-database-btn"
                    onClick={() => setPurgeDialog(true)}
                  >
                    <Trash2 className="h-4 w-4 mr-1.5" /> Purge & Reset
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============ USER MANAGEMENT ============ */}
          <TabsContent value="users" className="mt-6">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading text-base font-bold flex items-center gap-2">
                  <UserCog className="h-5 w-5 text-sky-500" /> User Management
                  <Badge variant="outline" className="ml-2 font-mono">{users.length} users</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs font-bold uppercase">Name</TableHead>
                        <TableHead className="text-xs font-bold uppercase">Email</TableHead>
                        <TableHead className="text-xs font-bold uppercase">Role</TableHead>
                        <TableHead className="text-xs font-bold uppercase">Status</TableHead>
                        <TableHead className="text-xs font-bold uppercase">Joined</TableHead>
                        <TableHead className="text-xs font-bold uppercase text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((u) => (
                        <TableRow key={u.id || u._id} data-testid={`user-row-${u.id || u._id}`}>
                          <TableCell className="font-medium text-sm">{u.name || "—"}</TableCell>
                          <TableCell className="text-sm text-slate-600">{u.email}</TableCell>
                          <TableCell>
                            <Badge className={ROLE_COLORS[u.role] || "bg-slate-100 text-slate-600"}>
                              {ROLE_LABELS[u.role] || u.role}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={u.status === "suspended" ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}>
                              {u.status || "active"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs text-slate-400">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</TableCell>
                          <TableCell className="text-right space-x-1">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              data-testid={`change-role-${u.id || u._id}`}
                              onClick={() => { setRoleDialog({ id: u.id || u._id, email: u.email, currentRole: u.role }); setSelectedRole(u.role); }}
                            >
                              <ShieldCheck className="h-3 w-3 mr-1" /> Role
                            </Button>
                            <Button
                              size="sm"
                              variant={u.status === "suspended" ? "default" : "destructive"}
                              className="h-7 text-xs"
                              data-testid={`toggle-status-${u.id || u._id}`}
                              onClick={() => handleStatusToggle(u.id || u._id, u.status || "active")}
                            >
                              {u.status === "suspended" ? <><CheckCircle2 className="h-3 w-3 mr-1" /> Activate</> : <><Ban className="h-3 w-3 mr-1" /> Suspend</>}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {users.length === 0 && (
                        <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No users found</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* Role Change Dialog */}
            <Dialog open={!!roleDialog} onOpenChange={(o) => !o && setRoleDialog(null)}>
              <DialogContent data-testid="role-change-dialog">
                <DialogHeader>
                  <DialogTitle>Change Role for {roleDialog?.email}</DialogTitle>
                </DialogHeader>
                <div className="py-4">
                  <Select value={selectedRole} onValueChange={setSelectedRole}>
                    <SelectTrigger data-testid="role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(ROLE_LABELS).map(([val, label]) => (
                        <SelectItem key={val} value={val}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setRoleDialog(null)}>Cancel</Button>
                  <Button onClick={handleRoleChange} disabled={selectedRole === roleDialog?.currentRole} data-testid="confirm-role-change">
                    Update Role
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* ============ AUDIT LOGS ============ */}
          <TabsContent value="audit" className="mt-6">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading text-base font-bold flex items-center gap-2">
                  <ScrollText className="h-5 w-5 text-amber-500" /> Audit Trail
                  <Badge variant="outline" className="ml-2 font-mono">{auditLogs.total} entries</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-xs font-bold uppercase">Timestamp</TableHead>
                      <TableHead className="text-xs font-bold uppercase">Action</TableHead>
                      <TableHead className="text-xs font-bold uppercase">User</TableHead>
                      <TableHead className="text-xs font-bold uppercase">Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(auditLogs.logs || []).map((log, i) => (
                      <TableRow key={i} data-testid={`audit-row-${i}`}>
                        <TableCell className="text-xs text-slate-500 whitespace-nowrap">{new Date(log.created_at).toLocaleString()}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="font-mono text-[11px]">{log.action}</Badge>
                        </TableCell>
                        <TableCell className="text-sm font-medium">{log.user_name || "System"}</TableCell>
                        <TableCell className="text-sm text-slate-600 max-w-xs truncate">{log.details}</TableCell>
                      </TableRow>
                    ))}
                    {(auditLogs.logs || []).length === 0 && (
                      <TableRow><TableCell colSpan={4} className="text-center py-8 text-slate-400">No audit logs</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
                {auditLogs.pages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
                    <span className="text-xs text-slate-400">Page {auditLogs.page} of {auditLogs.pages}</span>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" disabled={auditLogs.page <= 1} onClick={() => fetchAuditLogs(auditLogs.page - 1)} data-testid="audit-prev">
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="outline" disabled={auditLogs.page >= auditLogs.pages} onClick={() => fetchAuditLogs(auditLogs.page + 1)} data-testid="audit-next">
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============ FORUM MODERATION ============ */}
          <TabsContent value="forum" className="mt-6">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading text-base font-bold flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-violet-500" /> Forum Moderation
                  <Badge variant="outline" className="ml-2 font-mono">{forumPosts.total} posts</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-xs font-bold uppercase">Title</TableHead>
                      <TableHead className="text-xs font-bold uppercase">Author</TableHead>
                      <TableHead className="text-xs font-bold uppercase">Category</TableHead>
                      <TableHead className="text-xs font-bold uppercase">Posted</TableHead>
                      <TableHead className="text-xs font-bold uppercase text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(forumPosts.posts || []).map((post) => (
                      <TableRow key={post.id || post._id} data-testid={`forum-post-${post.id || post._id}`}>
                        <TableCell className="font-medium text-sm max-w-[200px] truncate">{post.title}</TableCell>
                        <TableCell className="text-sm text-slate-600">{post.author_name || "Guest"}</TableCell>
                        <TableCell><Badge variant="outline" className="text-[11px]">{post.category_slug}</Badge></TableCell>
                        <TableCell className="text-xs text-slate-400">{post.created_at ? new Date(post.created_at).toLocaleDateString() : "—"}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="destructive"
                            className="h-7 text-xs"
                            data-testid={`delete-post-${post.id || post._id}`}
                            onClick={() => handleDeletePost(post.id || post._id)}
                          >
                            <Trash2 className="h-3 w-3 mr-1" /> Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(forumPosts.posts || []).length === 0 && (
                      <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400">No forum posts</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Purge Database Confirmation Dialog */}
        <Dialog open={purgeDialog} onOpenChange={setPurgeDialog}>
          <DialogContent data-testid="purge-database-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="h-5 w-5" /> Confirm Database Purge
              </DialogTitle>
            </DialogHeader>
            <div className="py-3 space-y-3">
              <p className="text-sm text-slate-700">This will permanently:</p>
              <ul className="text-sm text-slate-600 space-y-1.5 ml-4 list-disc">
                <li>Delete <strong>all tenant/student accounts</strong></li>
                <li>Clear all applications, leases, payments, documents</li>
                <li>Clear all maintenance requests and audit logs</li>
                <li>Clear all forum posts and chat history</li>
                <li>Re-seed fresh rooms and forum categories</li>
              </ul>
              <p className="text-sm font-medium text-slate-800 mt-2">Staff accounts (admin, system admin, finance, property) will be preserved.</p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
                <p className="text-xs font-bold text-red-700">This action cannot be undone.</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPurgeDialog(false)} disabled={purging}>Cancel</Button>
              <Button
                variant="destructive"
                onClick={handlePurgeDatabase}
                disabled={purging}
                data-testid="confirm-purge-btn"
              >
                {purging ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Purging...</> : <><Trash2 className="h-4 w-4 mr-1.5" /> Purge Database</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
